import * as Icon from "@ant-design/icons";
import { Button, Checkbox, Col, DatePicker, Divider, Flex, Form, Image, Input, Row, Select, Space, Table } from "antd";
import Modal from "antd/es/modal/Modal";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { useEffect, useRef } from "react";
import { NumericFormat } from "react-number-format";
import MensagemModal from "../../../../components/modal/mensagem";
import AcoesComponent from "../../../../components/table/acoesComponent";
import { CategoriaContaBancoModel } from "../../../../models/categoriaContaBanco/categoriaContaBancoModel";
import { ContaBancoModel } from "../../../../models/contaBanco/contaBancoModel";
import { SaldoInicioContaBancoModel } from "../../../../models/contaBanco/saldoInicioContaBancoModel";
import { EmpresaModel } from "../../../../models/empresa/empresaModel";
import { ItemMenuAcoes } from "../../../../models/sistema/itemMenuAcoes";
import { setModalCadastrarContaBanco, setModalDeletarSaldoInicioContaBanco } from "../../../../redux/slices/contaBanco/contaBancoSlice";
import { setBuscaSaldoInicioContaBanco, setEntitySaldoInicioContaBanco } from "../../../../redux/slices/saldoInicioContaBanco/saldoInicioContaBancoSlice";
import { listarCategoriaContaBanco } from "../../../../services/categoriaContaBancoService";
import { atualizarContaBanco, cadastrarContaBanco } from "../../../../services/contaBancoService";
import { buscarEmpresa } from "../../../../services/empresaService";
import { buscarSaldoInicioContaBanco, cadastrarSaldoInicioContaBanco, deletarSaldoInicioContaBanco } from "../../../../services/saldoInicioContaBancoService";
import { currencyBRL, realParaNumber } from "../../../../util/dinheiroBRAuxiliar";
import { useAppDispatch, useAppSelector } from "../../../../util/hooks";

dayjs.locale("pt-br");

const CadastroContaBanco = () => {
  //estados globais
  const contaBancoState = useAppSelector((state) => state.contaBanco);
  const saldoInicioContaBancoState = useAppSelector((state) => state.saldoInicioContaBanco);
  const bancoState = useAppSelector((state) => state.banco);
  const empresaState = useAppSelector((state) => state.empresa);
  const categoriaContaBancoState = useAppSelector((state) => state.categoriaContaBanco);

  //hooks
  const dispatch = useAppDispatch();

  //estados locais
  const [form] = Form.useForm<ContaBancoModel>();
  const [formSaldoConta] = Form.useForm<SaldoInicioContaBancoModel>();

  const inputInicial = useRef<any>(null);

  const editar = Object.values(contaBancoState.entity).length !== 0;

  useEffect(() => {
    if (inputInicial.current && !editar) {
      inputInicial.current.focus();
    }
    if (empresaState.list.length === 0) dispatch(buscarEmpresa(empresaState.busca));
    if (categoriaContaBancoState.list.length === 0) dispatch(listarCategoriaContaBanco());

    if (editar) {
      form.setFieldsValue({
        ...contaBancoState.entity,
        dataInicio: dayjs(contaBancoState.entity.dataInicio),
      });
      dispatch(setBuscaSaldoInicioContaBanco(contaBancoState.entity.id));
      dispatch(buscarSaldoInicioContaBanco(contaBancoState.entity.id));
    } else {
      dispatch(buscarSaldoInicioContaBanco(null));
      form.setFieldsValue({
        geraContabil: true,
        ativo: true,
      });
    }
  }, [contaBancoState.entity]);

  //SALDO INICIO CONTA BANCO
  const goDeletarSaldoInicio = (value: SaldoInicioContaBancoModel) => {
    dispatch(setEntitySaldoInicioContaBanco(value));
    dispatch(setModalDeletarSaldoInicioContaBanco(true));
  };

  const closeModalDeletarSaldo = () => {
    dispatch(setModalDeletarSaldoInicioContaBanco(false));
  };

  const submitSaldoInicio = (value: any) => {
    let submit: SaldoInicioContaBancoModel = { ...value, idContaBanco: contaBancoState.entity.id, primeiroSaldo: true };
    dispatch(cadastrarSaldoInicioContaBanco({ value: submit, busca: saldoInicioContaBancoState.busca }));
  };

  const submit = async (primeiroForm: any, segundoForm: any) => {
    await form.validateFields();
    if (!editar) {
      await formSaldoConta.validateFields();
    }
    //Configuração de saldo inicial
    primeiroForm.dataSaldoInicio = segundoForm.data;
    primeiroForm.saldoInicial = segundoForm.saldoInicioDia;
    if (editar) {
      const updateContaBanco = { id: contaBancoState.entity.id, ...primeiroForm };
      dispatch(atualizarContaBanco({ value: updateContaBanco, busca: contaBancoState.busca }));
    } else {
      dispatch(cadastrarContaBanco({ value: primeiroForm, busca: contaBancoState.busca }));
    }
  };

  const voltar = () => {
    dispatch(setModalCadastrarContaBanco(false));
  };

  const items: ItemMenuAcoes[] = [
    {
      label: "Remover",
      key: "2",
      icon: <Icon.DeleteOutlined />,
      function: (item: any) => goDeletarSaldoInicio(item),
    },
  ];

  const columns: ColumnsType<SaldoInicioContaBancoModel> = [
    {
      title: "DATA",
      dataIndex: "data",
      render: (_, record) => {
        return <>{dayjs(record.data).format("DD/MM/YYYY")}</>;
      },
    },
    {
      title: "SALDO INICIAL",
      dataIndex: "saldoInicioDia",
      render: (_, record) => currencyBRL(record.saldoInicioDia),
    },
    {
      title: "AÇÕES",
      dataIndex: "acoes",
      align: "center",
      width: 75,
      render: (_, record) => <AcoesComponent record={record} items={items} />,
    },
  ];

  return (
    <Flex vertical>
      <Form layout="vertical" form={form}>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              label="Denominação"
              name="denominacaoPersonalizada"
              rules={[
                {
                  required: true,
                  message: "Digite uma denominação",
                },
              ]}
            >
              <Input type="text" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Empresa"
              name="idEmpresa"
              rules={[
                {
                  required: true,
                  message: "Escolha o nome da empresa",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="labelSearch"
                loading={empresaState.loading}
                options={empresaState.list.map((a: EmpresaModel) => ({
                  value: a.id,
                  key: a.apelido,
                  label: (
                    <Flex justify="flex-start" align="center" gap={8} style={{ padding: "1px 0px" }}>
                      <Flex style={{ lineHeight: "0px" }}>{a.urlLogotipoPequeno && <Image width={25} height={25} style={{ lineHeight: "0px" }} src={a.urlLogotipoPequeno} preview={false} />}</Flex>
                      {a.apelido}
                    </Flex>
                  ),
                  labelSearch: a.apelido,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Categoria"
              name="idCategoriaContaBanco"
              rules={[
                {
                  required: true,
                  message: "Escolha a categoria",
                },
              ]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                loading={categoriaContaBancoState.loading}
                options={categoriaContaBancoState.list.map((a: CategoriaContaBancoModel) => ({
                  value: a.id,
                  label: a.denominacao,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="ativo" label="Situação" valuePropName="checked">
              <Checkbox value="1" style={{ lineHeight: "32px" }}>
                Conta ativa
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12} align="bottom">
          <Col span={24}>
            <Flex gap={12}>
              <Form.Item label="Banco" name="idBanco">
                <Select
                  showSearch
                  loading={bancoState.loading}
                  style={{ width: 250 }}
                  optionFilterProp="label"
                  ref={inputInicial}
                  options={bancoState.list.map((a: any) => ({
                    value: a.id,
                    label: a.denominacao,
                    disabled: !a.ativo,
                  }))}
                />
              </Form.Item>
              <Form.Item label="Agência" name="agencia">
                <Input type="text" size="small" maxLength={4} style={{ textAlign: "right", width: 60 }} />
              </Form.Item>
              <span style={{ width: "10px", marginRight: -5, marginTop: 35 }}>-</span>
              <Form.Item label="Dígito" name="digitoAgencia">
                <Input type="text" size="small" maxLength={1} style={{ textAlign: "right", width: 30 }} />
              </Form.Item>

              <Form.Item label="Conta" name="numeroConta">
                <Input type="text" size="small" maxLength={5} style={{ textAlign: "right", width: 60 }} />
              </Form.Item>
              <span style={{ width: "10px", marginRight: -5, marginTop: 35 }}>-</span>
              <Form.Item label="Dígito" name="digitoConta">
                <Input type="text" size="small" maxLength={1} style={{ textAlign: "right", width: 30 }} />
              </Form.Item>

              <Form.Item name="geraContabil" label="    Gera informações contábeis" valuePropName="checked">
                <Checkbox value="1"></Checkbox>
              </Form.Item>
              <Form.Item label="Conta reduzida" name="contaReduzida">
                <Input type="text" size="small" />
              </Form.Item>
            </Flex>
          </Col>
        </Row>
      </Form>
      <Divider orientation="left" orientationMargin="0">
        {"Configuração de saldo inicial da conta"}
      </Divider>
      <Form layout="vertical" form={formSaldoConta}>
        <Row gutter={12} style={{ alignItems: "end" }}>
          <Col>
            <Form.Item label="Saldo inicial" name="saldoInicioDia" rules={[{ required: editar ? false : true, message: "digite um valor" }]}>
              <NumericFormat
                className="ant-input noBoderColor number-input"
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$"
                fixedDecimalScale={true}
                onChange={() => formSaldoConta.setFieldValue("saldoInicioDia", realParaNumber(formSaldoConta.getFieldValue("saldoInicioDia")))}
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item label="Data do saldo" name="data" rules={[{ required: editar ? false : true, message: "digite a data" }]}>
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
          <Col>
            {editar && (
              <Button onClick={() => submitSaldoInicio(formSaldoConta.getFieldsValue())} type="primary" style={{ marginBottom: 24 }} icon={<Icon.CheckCircleOutlined />}>
                Adicionar
              </Button>
            )}
          </Col>
        </Row>
        {editar && (
          <Row gutter={12}>
            <Col span={24}>
              <Table
                size="small"
                dataSource={saldoInicioContaBancoState.list}
                pagination={{
                  pageSize: 3,
                  showSizeChanger: true,
                }}
                rowClassName={(record, index) => (index === 0 ? "style: background:red" : "")}
                columns={columns}
                loading={saldoInicioContaBancoState.loading}
                rowKey="id"
                showSorterTooltip={false}
              />
            </Col>
          </Row>
        )}
      </Form>
      <Divider />
      <Row gutter={12}>
        <Col span={24} className="modalFooter">
          <Space size="middle">
            <Button className="cancelButton" onClick={voltar} icon={<Icon.CloseOutlined />}>
              FECHAR
            </Button>
            <Button onClick={() => submit(form.getFieldsValue(), formSaldoConta.getFieldsValue())} icon={<Icon.CheckOutlined />} className="buttonPrimary">
              {editar ? "ATUALIZAR" : "SALVAR"}
            </Button>
          </Space>
        </Col>
      </Row>
      <Modal title={"REMOVER SALDO"} centered width={350} open={contaBancoState.modalDeletarSaldoInicioContaBanco} onCancel={closeModalDeletarSaldo} footer={null} destroyOnHidden>
        <MensagemModal
          text="Deseja remover este saldo?"
          action={deletarSaldoInicioContaBanco({ value: saldoInicioContaBancoState.entity.id, busca: saldoInicioContaBancoState.busca })}
          close={setModalDeletarSaldoInicioContaBanco(false)}
        />
      </Modal>
    </Flex>
  );
};

export default CadastroContaBanco;
